import React, { Suspense, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import { Link } from "react-router-dom";
import LinkEntityModal from "src/components/AppLink/LinkEntityModal/LinkEntityModal";
import { apiOrganization } from "src/services/Api/Organization/apiOrganization";
import { getAppResourceTypeListOptions } from "src/utils/resourceTypes/getAppResourceTypeListOptions";
import { wrapPromise } from "go-core";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import handleError from "go-app/services/errors";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import EmptyList from "go-core/components/EmptyList";
import FormatBoolean from "go-core/components/Formatters/FormatBoolean";
import FormatDate from "go-core/components/Formatters/FormatDate";
import { LoadingContainer } from "go-core/components/Loading";
import RenderLimitedText from "go-core/components/RenderLimitedText";
import { useWindowSize } from "go-core/components/useWindowSize";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { ListConfig } from "go-list/list/services/types";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { AppLinkApi, LinkEntityApi } from "../../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../../services/Api/api";
import { AppResourceType } from "../../../../services/types";
import { getApplicationsHeaderTitle } from "../../../../utils/utils";
import { LinkingListType } from "../../services/types";

interface ListState {
	resource?: Record<string, any>;
	listType: LinkingListType;
	mobileActions: MobileActionProps[];
	productId?: number;
}

interface Props {
	listType: LinkingListType;
	productId?: number;
}

const listName = "APP_LINK";
const resourceType = "APP_LINK";

const getExternalSelectedFilters = (listType: LinkingListType) => {
	switch (listType) {
		case LinkingListType.TO_LINK:
			return [
				{
					id: "status",
					value: "OPEN",
					condition: "e",
					visible: true,
				},
				{
					id: "used",
					value: "true",
					condition: "true",
					visible: true,
				},
			];
		case LinkingListType.PRODUCT:
			return [
				{
					id: "resource_type",
					value: "PRODUCT",
					condition: "e",
					visible: true,
				},
			];
		case LinkingListType.CONTACT:
			return [
				{
					id: "resource_type",
					value: "CONTACT",
					condition: "e",
					visible: true,
				},
			];
		case LinkingListType.STOCK:
			return [
				{
					id: "resource_type",
					value: "STOCK",
					condition: "e",
					visible: true,
				},
			];
		case LinkingListType.INDEX:
		case LinkingListType.PRODUCT_APP_LINK:
		default:
			return [];
	}
};

const List = ({ resource, listType, mobileActions, productId }: ListState) => {
	const { t } = useTranslation();
	const [itemToLink, setItemToLink] = useState<LinkEntityApi | undefined>(undefined);
	const [params, setParams] = useState<Record<string, any>>({});
	const organization = useSelector(selectOrganization);
	const [items, setItems] = useState<AppLinkApi[]>([]);
	const segmentContextValue = React.useContext(SegmentContext);

	if (!resource) return null;
	const data = resource.read();

	const onHandleSave = async () => {
		setItemToLink(undefined);
		if (config.fetch) {
			const res = await config.fetch(params);
			setItems(res);
		}
	};

	const renderConnection = (item: AppLinkApi) => {
		let linkTo = "";

		switch (item.resource_type) {
			case AppResourceType.CONTACT:
				linkTo = `/${organization.id}/contacts/${item.resource_id}`;
				break;
			case AppResourceType.PRODUCT:
				linkTo = `/${organization.id}/products/${item.resource_id}/edit`;
				break;
			case AppResourceType.PRODUCT_CATEGORY:
				linkTo = `/${organization.id}/products/categories/${item.resource_id}/edit`;
				break;
			case AppResourceType.STOCK:
				linkTo = `/${organization.id}/settings/stocks/${item.resource_id}/edit`;
				break;
		}

		return (
			<Link target={"_blank"} rel={"noreferrer"} to={linkTo}>
				{item.resource_name}
			</Link>
		);
	};

	const getLinkedWithColumnValue = (item: AppLinkApi) => {
		if (item.skipped) {
			return <span className={"badge bg-warning text-dark"}>{t("modules.app_link.field.skipped.title")}</span>;
		}
		if (item.status === "OPEN") {
			return <span className={"badge bg-danger"}>{t("modules.app_link.field.unlinked.title")}</span>;
		}
		if (item.status === "CLOSED" || item.status === "TO_EXPORT") {
			return (
				<span>
					{t("modules.app_link.field.connected_with.title")} <>{renderConnection(item)}</>
				</span>
			);
		}
	};

	let config = {
		fields: [
			{
				id: "external_name",
				name: t("modules.app_link.field.external_name.title"),
				type: "text",
				render: (item: AppLinkApi) => {
					return item.link ? (
						<RenderLimitedText minWidth={200}>
							<a href={item.link} target={"_blank"} rel={"noreferrer"}>
								{item.external_name}
							</a>
						</RenderLimitedText>
					) : (
						<RenderLimitedText minWidth={200}>{item.external_name}</RenderLimitedText>
					);
				},
				renderExport: (item: AppLinkApi) => {
					return item.external_name;
				},
			},
			{
				id: "external_id",
				name: t("modules.app_link.field.external_id.title"),
				type: "text",
				render: (item: AppLinkApi) => <RenderLimitedText minWidth={200}>{item.external_id}</RenderLimitedText>,
				renderExport: (item: AppLinkApi) => item.external_id,
			},
			{
				id: "app",
				name: t("modules.app_link.field.app.title"),
				render: (item: AppLinkApi) => {
					return `${item.app?.name} (${t(`enums.apps.providers.${item.app.provider}`)})`;
				},
			},
			{
				id: "resource_type",
				name: t("modules.app_link.field.resource_type.title"),
				render: (item: AppLinkApi) => t(`enums.apps.resource_types.${item.resource_type}`),
			},
			{
				id: "linked_with",
				name: t("modules.app_link.field.connected_with.title"),
				render: (item: AppLinkApi) => getLinkedWithColumnValue(item),
				renderExport: (item: AppLinkApi) => {
					if (item.skipped) return t("modules.app_link.field.skipped.title");

					if (item.status === "OPEN") return t("modules.app_link.field.unlinked.title");

					if (item.status === "CLOSED" || item.status === "TO_EXPORT")
						return t("modules.app_link.field.connected_with.title");
				},
			},
			{
				id: "used",
				name: t("modules.app_link.field.used.title"),
				type: "boolean",
				render: (item: AppLinkApi) => <FormatBoolean value={item.used} />,
				renderExport: (item: AppLinkApi) => (item.used ? t("lib:common.word.yes") : t("lib:common.word.no")),
			},
			{
				id: "updated_at",
				name: t("modules.app_link.field.updated_at.title"),
				render: (item: AppLinkApi) => FormatDate(item.updated_at),
				renderExport: (item: AppLinkApi) => FormatDate(item.updated_at),
			},
			{
				id: "imported_at",
				name: t("modules.app_link.field.imported_at.title"),
				render: (item: AppLinkApi) => FormatDate(item?.imported_at),
				renderExport: (item: AppLinkApi) => FormatDate(item?.imported_at),
			},
			{
				id: "exported_at",
				name: t("modules.app_link.field.exported_at.title"),
				render: (item: AppLinkApi) => FormatDate(item?.exported_at),
				renderExport: (item: AppLinkApi) => FormatDate(item?.exported_at),
			},
		],
		filters: [
			{
				id: "status",
				name: t("lib:common.word.status"),
				type: "list",
				listOptions: {
					OPEN: t("enums.common.status.OPEN"),
					CLOSED: t("enums.common.status.CLOSED"),
					TO_EXPORT: t("enums.common.status.TO_EXPORT"),
				},
				hasDefaultOptions: false,
			},
			{
				id: "id",
				name: "Id",
				type: "number",
			},
			{
				id: "skipped",
				name: t("modules.app_link.field.skipped.title"),
				type: "boolean",
			},
			{
				id: "resource_type",
				name: t("modules.app_link.field.resource_type.title"),
				type: "list",
				listOptions: getAppResourceTypeListOptions(t),
				hasDefaultOptions: false,
			},
			{
				id: "app",
				name: t("modules.app_link.field.app.title"),
				type: "search_select",
				render: (item: AppLinkApi) => {
					return `${item.app?.name} (${t(`enums.apps.providers.${item.app.provider}`)})`;
				},
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						api.organization().getAppsSearchSelect(search, params, {
							cancelToken: options?.token,
						}),
					render: (item: AppLinkApi) => {
						return `${item.label} (${t(`enums.apps.providers.${item.provider}`)})`;
					},
				},
				hasDefaultOptions: false,
			},
		],
		actions: [
			{
				name: t("common.action.connect"),
				click: (item: AppLinkApi) => {
					const modifiedItem = { ...item, integration_channel: {} };
					setItemToLink(modifiedItem as LinkEntityApi);
				},
			},
		],
		selectedColumns: ["external_name", "external_id", "app", "resource_type", "linked_with", "used", "updated_at"],
		externalSelectedFilters: getExternalSelectedFilters(listType),
		segments: [
			{
				id: "all",
				name: t("common.word.all", { ns: "lib" }),
				slug: "all",
			},
		],
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		exportConfig: {
			title: t("modules.app_link.field.export_config.title"),
			filename: t("modules.app_link.header.title"),
			organization: `${organization.name}`,
		},
		fetch: async (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			setParams(params);
			params.include = "app,resource";

			return listType === LinkingListType.PRODUCT_APP_LINK && productId
				? apiOrganization.getProductLinks(productId, params, { cancelToken: sourceToken?.token })
				: apiOrganization.getLinks(params, { cancelToken: sourceToken?.token });
		},
		saveSegment: async (segment: SegmentType) => {
			return segmentContextValue.save(listName, resourceType, segment);
		},
		doesIdColumnRedirectToPreviewPage: true,
		numberOfStickyColumnsAtTheStart: 1,
	} as ListConfig;
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		customFields: data.fields,
		filterValues: data.filter_values,
	};
	return (
		<>
			{itemToLink && (
				<LinkEntityModal
					onSuccess={onHandleSave}
					onHide={() => setItemToLink(undefined)}
					isShown={!!itemToLink}
					item={itemToLink}
				/>
			)}
			<ListData
				data={items}
				config={config}
				emptyList={
					<EmptyList
						title={t("modules.app_link.header.title")}
						description={t("modules.app_link.field.empty_list_description.title")}
					/>
				}
				onFetch={(items: AppLinkApi[]) => setItems(items)}
				mobileActions={mobileActions}
			/>
		</>
	);
};

export const OrganizationAppsLinkingPage = ({ listType, productId }: Props) => {
	const { t } = useTranslation();
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContextValue = React.useContext(SegmentContext);
	const { addFlash, addSuccessFlash } = useFlash();
	const [loading, setLoading] = useState(false);
	const isMobile = useWindowSize().isMobile;
	const location = useLocation();
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		handleChangeTabTitle(getApplicationsHeaderTitle(location.pathname, t));
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType)));
	}, []);

	const syncApplications = async () => {
		setLoading(true);
		try {
			await api.organization().syncApps();
			addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
		} catch (e) {
			handleError.alert(e, addFlash);
		}
		setLoading(false);
	};

	const buttons: ButtonProps[] = [
		{
			title: t("common.action.sync", { ns: "lib" }),
			loading,
			variant: "primary",
			action: syncApplications,
			hide: listType === LinkingListType.PRODUCT_APP_LINK,
		},
	];
	const mobileActions: MobileActionProps[] = [
		{
			title: t("common.action.sync", { ns: "lib" }),
			action: syncApplications,
			hide: listType === LinkingListType.PRODUCT_APP_LINK,
		},
	];

	return (
		<>
			{!isMobile && listType !== LinkingListType.PRODUCT_APP_LINK && (
				<Header title={getApplicationsHeaderTitle(location.pathname, t)} buttons={buttons} />
			)}
			<Suspense fallback={<LoadingContainer />}>
				<List resource={resource} listType={listType} mobileActions={mobileActions} productId={productId} />
			</Suspense>
		</>
	);
};

// t("enums.apps.providers.GOPOS")
// t("enums.apps.providers.GOPOS_V2")
// t("enums.apps.providers.GASTROSUPPLIER")
// t("enums.apps.providers.GASTROSUPPLIER_MERCHANT")
// t("enums.apps.providers.DOCUMENT_TRANSFER")
// t("enums.apps.providers.LOCATIONS")
// t("enums.apps.providers.DOCUMENT_FILE_IMPORT")
